import { Link } from "react-router-dom";
import SEOHead from "@/components/base/SEOHead";
import ArticleCard from "@/components/base/ArticleCard";
import { siteInfo } from "@/mocks/siteInfo";
import { categories } from "@/mocks/categories";
import { getFeaturedArticles, getRecentArticles } from "@/mocks/articles";

const featuredArticles = getFeaturedArticles();
const recentArticles = getRecentArticles(6);

export default function Home() {
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    contactPoint: {
      "@type": "ContactPoint",
      email: siteInfo.contact.email,
      contactType: "customer service",
    },
  };

  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteInfo.url}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: siteInfo.title,
    description: siteInfo.description,
    url: siteInfo.url,
    isPartOf: { "@id": `${siteInfo.url}/#website` },
    about: { "@id": `${siteInfo.url}/#organization` },
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [orgSchema, webSiteSchema, webPageSchema],
  };

  return (
    <>
      <SEOHead
        title={siteInfo.title}
        description={siteInfo.description}
        keywords={siteInfo.keywords}
        canonicalUrl={siteInfo.url}
        ogImage="https://readdy.ai/api/search-image?query=Chinese%20female%20photography%20model%20elegant%20portrait%20with%20flowers%2C%20professional%20photography%20website%20banner%2C%20soft%20natural%20lighting%2C%20warm%20atmosphere%2C%20clean%20minimalist%20design%2C%20beautiful%20composition&width=1200&height=630&seq=home-og-2025&orientation=landscape"
        schemaData={combinedSchema}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20female%20model%20portrait%20with%20flowing%20fabric%20in%20soft%20wind%2C%20dreamy%20natural%20lighting%2C%20minimalist%20cream%20background%2C%20professional%20editorial%20photography%2C%20gentle%20expression%2C%20artistic%20composition%2C%20warm%20neutral%20tones%2C%20fashion%20magazine%20quality&width=1600&height=800&seq=hero-bailu-2025&orientation=landscape"
          alt={siteInfo.title}
          title={siteInfo.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-wide">
            国模白露写真馆
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl leading-relaxed">
            专业写真摄影作品欣赏与风格解析平台<br className="hidden md:block" />
            汇集白露多风格写真作品，为摄影爱好者提供灵感与参考
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/category/qingxin-xiezhen"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览作品
            </Link>
            <Link
              to="/category/paishe-jiqiao"
              className="px-6 py-2.5 bg-background-50/20 text-white border border-white/30 rounded-full text-sm font-medium hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              学习技巧
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 md:py-10 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {siteInfo.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary-500">
                  {stat.value}
                </div>
                <div className="text-xs md:text-sm text-foreground-500 mt-1">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Section */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
              作品分类
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              按风格与类型浏览白露的写真作品
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.slice(0, 8).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300/60 hover:bg-background-100 transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200/70 transition-colors mb-3">
                  <i className="ri-image-line text-2xl text-accent-600"></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                精选推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                编辑精选的高质量写真作品
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                最新内容
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                持续更新的写真作品与摄影技巧
              </p>
            </div>
            <Link
              to="/category/qingxin-xiezhen"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentArticles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* About Brief */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              关于白露写真馆
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
              {siteInfo.brandIntro}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-5 py-2 bg-secondary-500 text-background-50 rounded-full text-sm font-medium hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              了解更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}